<template>
  <div class="map-container fill-height">
    <div v-once id="map" ref="container" />
    <slot v-if="ready && mapLayersConfigData" />
  </div>
</template>

<script setup lang="ts">
import mapboxgl, { type RequestTransformFunction } from 'mapbox-gl'
import { onMounted, onUnmounted, provide, ref, shallowRef, toRef, watch } from 'vue'
import { useState } from 'vuex-composition-helpers'
import { usePersistedFieldViewPosition } from '../usePersistedFieldViewPosition'
import { MapContextKey } from './context'
import { getInstance } from '@/auth'
import type { MapboxRequestParams, RequestInterceptor } from './RequestInterceptor'
import { useJobFilterStore } from '../JobsFilterStore'
import { isEqual } from 'lodash'
import { useGetMapLayersQuery } from '@/api/mapping'
import { useNavStore } from '@/components/nav/navStore'
import { useMapImages } from './useMapImages'
import { useCreateMapHoverState } from './MapHoverState'

const props = withDefaults(
  defineProps<{
    center?: mapboxgl.LngLatLike
    zoom?: number
    hash?: boolean
    mapStyle?: string
    rememberPosition?: boolean
    mini?: boolean
  }>(),
  {
    center: undefined,
    zoom: undefined,
    hash: false,
    mapStyle: 'mapbox://styles/mapbox/standard-satellite',
    rememberPosition: false,
    mini: false,
  },
)

const emits = defineEmits<{
  (event: 'clickFeature', features: mapboxgl.GeoJSONFeature[], eventData: mapboxgl.MapMouseEvent)
  (event: 'clickCluster', data: number[])
  (event: 'sourceLoaded', sourceId: string)
  (event: 'move', data: mapboxgl.LngLatLike)
}>()

const map = shallowRef<mapboxgl.Map>()
const popup = shallowRef<mapboxgl.Popup>()
useCreateMapHoverState()

const container = ref<HTMLElement>()

const ready = ref<boolean>()
const { previousPosition, saveMapPosition } = usePersistedFieldViewPosition(map)

const { mapboxAccessToken, mapCenterPoint } = useState('organizationConfig', [
  'mapboxAccessToken',
  'mapCenterPoint',
])

const authService = ref()
const userAuthToken = ref<string>()

const { data: mapLayersConfigData } = useGetMapLayersQuery()

const authenticationInterceptor: RequestInterceptor = (url, resourceType, currentRequest) => {
  if (resourceType === 'Source' && url.startsWith(window.location.origin)) {
    return {
      ...currentRequest,
      headers: {
        ...currentRequest.headers,
        Authorization: `Bearer ${userAuthToken.value}`,
      },
    }
  }

  return currentRequest
}

const requestHandlers = ref<{ [key: string]: RequestInterceptor }>({
  auth: authenticationInterceptor,
})

const addRequestHandler = (key: string, handler: RequestInterceptor) => {
  requestHandlers.value[key] = handler
}

const removeRequestHandler = (key: string) => {
  delete requestHandlers.value[key]
}

const handleMapLoaded = async (
  activeMap: mapboxgl.Map,
  mapImages: ReturnType<typeof useMapImages>,
) => {
  mapImages.registerMissingImageHandler()
  await mapImages.ensureArrowImages()

  activeMap.addControl(new mapboxgl.NavigationControl(), 'bottom-right')

  ready.value = true
}

const initializeMap = async () => {
  if (map.value || !mapboxAccessToken.value) return

  authService.value = getInstance()
  userAuthToken.value = await authService.value.getTokenSilently()

  const fallbackCenter: mapboxgl.LngLatLike = {
    lat: mapCenterPoint.value.latitude,
    lng: mapCenterPoint.value.longitude,
  }
  const fallbackZoom = mapCenterPoint.value.zoom

  const defaultPosition: {
    center: mapboxgl.LngLatLike
    zoom: number
  } = {
    center: props.center ?? previousPosition.value?.center ?? fallbackCenter,
    zoom: props.zoom ?? previousPosition.value?.zoom ?? fallbackZoom,
  }

  mapboxgl.accessToken = mapboxAccessToken.value
  const activeMap = new mapboxgl.Map({
    container: container.value as HTMLElement,
    style: props.mapStyle,
    center: defaultPosition.center,
    zoom: defaultPosition.zoom,
    hash: props.hash,
    transformRequest: onTransformOutgoingRequest,
    config: {
      // https://docs.mapbox.com/map-styles/standard/api/#standard-satellite
      basemap: {
        showPointOfInterestLabels: false,
        showTransitLabels: false,
      },
    },
  })
  const mapImages = useMapImages(activeMap)
  map.value = activeMap
  activeMap
    .on('load', (e) => {
      handleMapLoaded(e.target, mapImages).catch((error) => {
        setTimeout(() => {
          throw error
        })
      })
    })
    .on('sourcedata', (e) => {
      if (
        e &&
        'isSourceLoaded' in e &&
        e.isSourceLoaded &&
        'sourceId' in e &&
        ['locations', 'resources'].includes(e.sourceId ?? '')
      ) {
        emits('sourceLoaded', e.sourceId as string)
      }
    })
    .on('move', (e) => {
      emits('move', e.target.getCenter())
    })
    .on('moveend', () => {
      if (props.rememberPosition) saveMapPosition()
    })

  popup.value = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
  })
}

const onTransformOutgoingRequest: RequestTransformFunction = (url, resourceType) => {
  return Object.values(requestHandlers.value).reduce<MapboxRequestParams>(
    (result, nextHandler) => {
      return {
        ...result,
        ...nextHandler(url, resourceType, result),
      }
    },
    { url },
  )
}

const isMini = toRef(props, 'mini')

const store = useJobFilterStore()
onMounted(() => {
  initializeMap()
})

onUnmounted(() => {
  if (map.value) map.value.remove()

  if (popup.value) popup.value.remove()

  if (authService.value) authService.value = null
})

watch(() => mapboxAccessToken, initializeMap)
// When the map is mounted, it is rendered fixed to a certain dimension/size
// Therefore, whenever we are showing or hiding the job filter side panel, we need to resize the map
// Otherwise, showing/hiding the side panel may make the map not fit into the entire screen
watch(
  () => store.isShowingFilters,
  () => {
    map.value?.resize()
  },
)

const navStore = useNavStore()
watch(
  () => navStore.isNavVisible,
  () => {
    map.value?.resize()
  },
)

watch(
  () => props.center,
  (newCenter, oldCenter) => {
    if (map.value && newCenter && !isEqual(newCenter, oldCenter)) {
      map.value.jumpTo({ center: newCenter })
    }
  },
)

provide(MapContextKey, {
  map,
  popup,
  mini: isMini,
  addRequestHandler,
  removeRequestHandler,
})

defineExpose({ map })
</script>

<style scoped>
.map-container {
  position: relative;
  height: 100%;
}

#map {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

#map :focus {
  outline: none;
}
</style>
